"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, CloudRain, SunMedium, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const seasonalityCycles = [{
  season: "Monsoon Peak",
  months: "June – September",
  icon: CloudRain,
  highlight: "Highest ADR & Leisure Surge",
  description: "Massive influx from Mumbai and Pune seeking scenic escapes and waterfalls. Dynamic minimum length-of-stay (MLOS) and strict advance-booking fences capitalize on peak weekend compression."
}, {
  season: "Winter & Festive Surge",
  months: "October – January",
  icon: Sparkles,
  highlight: "Festive Holidays & Wedding Buyouts",
  description: "High-value family holidays, Diwali breaks, Christmas/New Year getaways, and private destination events. Premium tiered pricing captures top-tier leisure budgets."
}, {
  season: "Summer & Shoulder Season",
  months: "February – May",
  icon: SunMedium,
  highlight: "Corporate Offsites & Weekday Yield",
  description: "Focus shifts to weekday MICE, corporate team offsites, and localized weekend staycations through targeted packaging and value-add promotions."
}];
const challenges = [{
  id: 1,
  title: "Weekend vs. Midweek Gap Compression",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Manage sharp weekend occupancy spikes against sluggish midweek lulls typical of drive-to hill stations. We construct dynamic length-of-stay controls and targeted corporate/staycation packages so properties do not rely on panic discounting.",
  metricLabel: "Strategy focus",
  metricValue: "Dynamic MLOS controls & corporate offsite midweek fencing"
}, {
  id: 2,
  title: "Aggressive Comp-Set & Villa Undercutting",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Track intraday rate shifts across MakeMyTrip, Agoda, and Booking.com amidst dense luxury villa and boutique resort competition. Automated rate alerts and yield rules protect your RevPAR while securing high-intent bookings.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA monitoring & rule-based rate fencing"
}, {
  id: 3,
  title: "The OTA Commission & Parity Trap",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Stop unauthorized wholesaler rate leakage and reclaim margins lost to high OTA commissions. We enforce strict rate parity across metasearch engines and drive direct drive-market guest bookings through tailored incentives.",
  metricLabel: "Strategy focus",
  metricValue: "Wholesaler rate fencing & direct drive-in booking boosters"
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapelonavala" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Lonavala
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            As a prime getaway hub in the Western Ghats connecting Mumbai and Pune, Lonavala operates on intense weekend compressions, hyper-seasonal monsoon peaks, and fierce competition among luxury resorts and private villas. Sunil Singh implements tailored revenue architectures to maximize RevPAR across every operating cycle.
          </p>
        </div>

        {/* Seasonality Block */}
        <div className="mb-16 md:mb-20 rounded-xl border border-border bg-card p-6 md:p-8 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4 border-b border-border/60 pb-6">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                Lonavala Demand Cycles
              </span>
              <h3 className="font-serif text-2xl sm:text-3xl font-bold text-foreground mt-1">
                Seasonality & Yield Windows
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              Targeted pricing fences and minimum-stay rules aligned to regional drive-market travel rhythms.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {seasonalityCycles.map((item, index) => {
            const IconComponent = item.icon;
            return <div key={item.season} data-index={index} className="rounded-lg border border-border/70 bg-muted/20 p-5 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="inline-flex size-10 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                        <IconComponent className="size-5 text-chart-2" />
                      </span>
                      <Badge variant="secondary" className="text-xs font-mono">
                        {item.months}
                      </Badge>
                    </div>
                    <h4 className="font-serif text-lg font-bold text-foreground mb-1">
                      {item.season}
                    </h4>
                    <p className="text-xs font-semibold text-secondary mb-3">
                      {item.highlight}
                    </p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Lonavala hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}